import { API_ENDPOINTS } from "@/config/api";
import { fetchApi } from "./api";

export type NewsletterSubmissionStatus =
  | "draft"
  | "submitted"
  | "approved"
  | "rejected"
  | "included";
export type NewsletterIssueStatus = "draft" | "review" | "scheduled" | "sent";

export interface NewsletterAttachment {
  /** Canonical S3 key (storage ref, not a URL). */
  key?: string;
  /** Ephemeral presigned GET URL — response-only, not persisted. */
  url?: string;
  /** Human-readable filename from the original upload. */
  original_name?: string;
  kind?: "image" | "pdf" | "document";
  // Legacy label fallbacks for data that predates the S3 migration
  filename?: string;
  name?: string;
  path?: string;
  size?: number;
  content_type?: string;
}

export interface NewsletterSubmission {
  id: number;
  business_id: number;
  issue_id: number | null;
  status: NewsletterSubmissionStatus | string;
  headline: string | null;
  blurb: string | null;
  cta_url: string | null;
  cta_label: string | null;
  /** Opaque S3 key — use only as an existence guard, not for display URLs. */
  image_path: string | null;
  /** Presigned GET URL for the hero image — use this for display/linking. */
  image_url: string | null;
  attachments: NewsletterAttachment[] | null;
  admin_notes: string | null;
  submitted_at: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface ListNewsletterSubmissionsParams {
  status_filter?: string;
  business_id?: number;
}

export interface PatchNewsletterSubmissionRequest {
  admin_notes?: string | null;
  status?: "approved" | "rejected" | NewsletterSubmissionStatus | string;
}

export interface NewsletterIssue {
  id: number;
  title: string;
  subject: string | null;
  status: NewsletterIssueStatus | string;
  intro_html: string | null;
  block_order: number[] | null;
  scheduled_send_at: string | null;
  sent_at: string | null;
  recipient_count: number | null;
  created_at: string;
  updated_at: string | null;
}

export interface NewsletterIssueDetails extends NewsletterIssue {
  rendered_html: string | null;
  rendered_text: string | null;
}

export interface CreateNewsletterIssueRequest {
  title: string;
  subject?: string | null;
  intro_html?: string | null;
}

export interface PatchNewsletterIssueRequest {
  title?: string;
  subject?: string | null;
  intro_html?: string | null;
  block_order?: number[];
}

export interface RenderNewsletterIssueResponse {
  status: string;
  rendered: boolean;
}

export interface TestSendNewsletterIssueResponse {
  sent: boolean;
}

export interface SendNewsletterIssueResponse {
  job_id: string | null;
  issue_id: number;
}

export async function listNewsletterSubmissions(
  params?: ListNewsletterSubmissionsParams,
): Promise<NewsletterSubmission[]> {
  const search = new URLSearchParams();
  if (params?.status_filter) search.set("status_filter", params.status_filter);
  if (params?.business_id != null)
    search.set("business_id", String(params.business_id));
  const query = search.toString();
  const url = query
    ? `${API_ENDPOINTS.newsletterSubmissions}?${query}`
    : API_ENDPOINTS.newsletterSubmissions;
  return fetchApi<NewsletterSubmission[]>(url);
}

export async function patchNewsletterSubmission(
  submissionId: number,
  patch: PatchNewsletterSubmissionRequest,
): Promise<NewsletterSubmission> {
  return fetchApi<NewsletterSubmission>(
    API_ENDPOINTS.newsletterSubmission(submissionId),
    {
      method: "PATCH",
      body: JSON.stringify(patch),
    },
  );
}

export async function listNewsletterIssues(): Promise<NewsletterIssue[]> {
  return fetchApi<NewsletterIssue[]>(API_ENDPOINTS.newsletterIssues);
}

export async function getNewsletterIssue(
  issueId: number,
): Promise<NewsletterIssueDetails> {
  return fetchApi<NewsletterIssueDetails>(
    API_ENDPOINTS.newsletterIssue(issueId),
  );
}

export async function createNewsletterIssue(
  data: CreateNewsletterIssueRequest,
): Promise<NewsletterIssue> {
  return fetchApi<NewsletterIssue>(API_ENDPOINTS.newsletterIssues, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function patchNewsletterIssue(
  issueId: number,
  patch: PatchNewsletterIssueRequest,
): Promise<NewsletterIssue> {
  return fetchApi<NewsletterIssue>(API_ENDPOINTS.newsletterIssue(issueId), {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export async function renderNewsletterIssue(
  issueId: number,
): Promise<RenderNewsletterIssueResponse> {
  return fetchApi<RenderNewsletterIssueResponse>(
    API_ENDPOINTS.newsletterIssueRender(issueId),
    { method: "POST" },
  );
}

export async function testSendNewsletterIssue(
  issueId: number,
  recipientEmail: string,
): Promise<TestSendNewsletterIssueResponse> {
  return fetchApi<TestSendNewsletterIssueResponse>(
    API_ENDPOINTS.newsletterIssueTestSend(issueId),
    {
      method: "POST",
      body: JSON.stringify({ recipient_email: recipientEmail }),
    },
  );
}

export async function sendNewsletterIssue(
  issueId: number,
): Promise<SendNewsletterIssueResponse> {
  return fetchApi<SendNewsletterIssueResponse>(
    API_ENDPOINTS.newsletterIssueSend(issueId),
    { method: "POST" },
  );
}
